import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { InventoryReportsService, InventoryReport } from '../../services/inventory-reports.service';

@Component({
  selector: 'app-inventory-reports',
  templateUrl: './inventory-reports.component.html',
  styleUrls: ['./inventory-reports.component.css']
})
export class InventoryReportsComponent implements OnInit {
  displayedColumns: string[] = ['id', 'productCode', 'productName', 'category', 'currentStock', 'minStockLevel', 'maxStockLevel', 'unitPrice', 'totalValue', 'lastRestockDate', 'stockStatus', 'location', 'supplier', 'actions'];
  dataSource = new MatTableDataSource<InventoryReport>();
  isLoading = false;
  selectedDate: Date = new Date();
  totalProducts: number = 0;
  totalValue: number = 0;
  lowStockItems: number = 0;
  outOfStockItems: number = 0;

  constructor(
    private inventoryReportsService: InventoryReportsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.loadInventoryReports();
  }

  loadInventoryReports(): void {
    this.isLoading = true;
    this.inventoryReportsService.getInventoryReports().subscribe({
      next: (response) => {
        if (response.success) {
          this.dataSource.data = response.data;
          this.calculateTotals();
        } else {
          this.snackBar.open(response.message || 'Failed to load inventory reports', 'Close', { duration: 3000 });
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading inventory reports:', error);
        this.snackBar.open('Error loading inventory reports', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  calculateTotals(): void {
    this.totalProducts = this.dataSource.data.length;
    this.totalValue = this.dataSource.data.reduce((sum, item) => sum + item.totalValue, 0);
    this.lowStockItems = this.dataSource.data.filter(item => item.stockStatus === 'Low Stock').length;
    this.outOfStockItems = this.dataSource.data.filter(item => item.stockStatus === 'Out of Stock').length;
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  dateChange(event: MatDatepickerInputEvent<Date>): void {
    this.selectedDate = event.value || new Date();
    this.loadInventoryReports(); // Reload with new date filter
  }

  generateReport(): void {
    this.snackBar.open('Generating inventory report...', 'Close', { duration: 3000 });
    // TODO: Implement report generation
  }

  exportToExcel(): void {
    this.snackBar.open('Exporting to Excel...', 'Close', { duration: 3000 });
    this.inventoryReportsService.exportToExcel().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `inventory-reports-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.snackBar.open('Excel export completed', 'Close', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error exporting to Excel:', error);
        this.snackBar.open('Error exporting to Excel', 'Close', { duration: 3000 });
      }
    });
  }

  exportToPDF(): void {
    this.snackBar.open('Exporting to PDF...', 'Close', { duration: 3000 });
    this.inventoryReportsService.exportToPDF().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `inventory-reports-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.snackBar.open('PDF export completed', 'Close', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error exporting to PDF:', error);
        this.snackBar.open('Error exporting to PDF', 'Close', { duration: 3000 });
      }
    });
  }

  viewProductDetails(product: InventoryReport): void {
    this.snackBar.open(`Viewing details for ${product.productName}`, 'Close', { duration: 3000 });
    // TODO: Implement product details dialog
  }

  reorderProduct(product: InventoryReport): void {
    this.snackBar.open(`Creating reorder for ${product.productName}...`, 'Close', { duration: 3000 });
    // TODO: Implement reorder functionality
  }

  getStockStatusBadgeClass(status: string): string {
    switch (status) {
      case 'Normal': return 'badge-success';
      case 'Low Stock': return 'badge-warning';
      case 'Out of Stock': return 'badge-danger';
      case 'Overstock': return 'badge-info';
      default: return 'badge-secondary';
    }
  }

  filterByStatus(status: string): void {
    if (status === 'All') {
      this.loadInventoryReports();
    } else {
      const filteredData = this.dataSource.data.filter(item => item.stockStatus === status);
      this.dataSource.data = filteredData;
    }
  }
}
